import { Injectable, inject } from '@angular/core';
import {
    Client,
    StudentProfileResponse,
    UpdateStudentRequest,
    RegisteredStudent,
    UpdateStudentEmailResponse,
    UpdateStudentEmailRequest
} from '../api/client-student.service';
import { saveAs } from 'file-saver';
import { CacheService } from './cache.service';
import { firstValueFrom } from 'rxjs';

export class HttpOptions {
    url: string;
    body?: any;
    cacheMins?: number;
}

@Injectable()
export class StudentService {
    private client = inject(Client);
    private _cacheService = inject(CacheService);


    async student(options: HttpOptions): Promise<StudentProfileResponse> {
        // Setup default values
        options.body = options.body || null;
        options.cacheMins = options.cacheMins || 0;

        if (options.cacheMins > 0) {
            const data = this._cacheService.load(options.url);
            // Return data from cache
            if (data !== null) {
                return data;
            }
        }

        const response = await firstValueFrom(this.client.get()) as StudentProfileResponse;
        if (options.cacheMins > 0) {
            // Data will be cached
            this._cacheService.save({
                key: options.url,
                data: response,
                expirationMins: options.cacheMins
            });
        }
        return response;
    }

    clearCache(key: any) {
      this._cacheService.remove(key);
    }

    async studentUpdate(registerProfileData: any): Promise<RegisteredStudent> {
        const updateStudentRequest = new UpdateStudentRequest();
        updateStudentRequest.init({
            lui: registerProfileData.lui,
            firstName: registerProfileData.firstName,
            lastName: registerProfileData.lastName,
            dateOfBirth: registerProfileData.dob,
            phone: registerProfileData.phone,
            shareDataWithSchool: registerProfileData.optIn,
            receiveResultsByEmail: registerProfileData.sendEmail,
            researchAndAnalysisConsentAccepted: registerProfileData.researchAndAnalysisConsentAccepted,
        });

        return await firstValueFrom(this.client.update(updateStudentRequest)) as RegisteredStudent;
    }

    async studentEmailUpdate(studentEmailEditData: any): Promise<UpdateStudentEmailResponse> {
        const updateStudentEmailRequest = new UpdateStudentEmailRequest();
        updateStudentEmailRequest.init({
            email: studentEmailEditData.currentEmail,
            newEmail: studentEmailEditData.newEmail
        });

        return await firstValueFrom(this.client.updateEmail(updateStudentEmailRequest)) as UpdateStudentEmailResponse;
    }

    async studentShareDataUpdate(optIn: boolean): Promise<RegisteredStudent> {
        const updateStudentRequest = new UpdateStudentRequest();
        updateStudentRequest.init({
            shareDataWithSchool: optIn
        });

        return await firstValueFrom(this.client.update(updateStudentRequest)) as RegisteredStudent;
    }

    async studentSendEmailUpdate(optIn: boolean): Promise<RegisteredStudent> {
        const updateStudentRequest = new UpdateStudentRequest();
        updateStudentRequest.init({
            receiveResultsByEmail: optIn
        });

        return await firstValueFrom(this.client.update(updateStudentRequest)) as RegisteredStudent;
    }

    async downloadCertificate(exitYear: number, firstName: string, lastName: string): Promise<void> {
        const filename = `${firstName}_${lastName}_${exitYear}_ATAR.pdf`;
        try {
            const data = await firstValueFrom(this.client.getCertificatePdf(exitYear));
            saveAs(new Blob([data.data], { type: 'application/pdf' }), filename);
        } catch (error: any) {
            if (error.status === 403) {
                throw new Error('Payment required to download certificate');
            }
            throw new Error('Unable to download certificate');
        }
    }
}
